<template>
  <footer class="footer">
    <div class="footer__inner">
      <div class="footer__col footer__col--left">
        <a class="footer__brand" href="https://mrdn.world" target="_blank" rel="noopener">
          <img class="footer__logo" src="/meridien-logo-footer.svg" alt="Méridien" width="89" height="51" />
        </a>
        <p class="footer__copy">© 2026 Méridien Immersive Media</p>
        <p class="footer__copy">All rights reserved</p>

        <p class="footer__about">
          <strong>Méridien is a media project about unique places and breathtaking
          cultural landmarks around the globe.</strong>
          Together with our readers, we travel across meridians and latitudes, sharing
          inspiring stories of civilizational breakthroughs — innovations in human
          engineering, architecture, culture, and technological advancement.
        </p>
      </div>

      <div class="footer__col footer__col--center">
        <p class="footer__updated">Published: 12 May 2026</p>
        <div class="footer__about footer__about--muted">
          <p>
            We combine modern visual technologies, immersive digital reality, storytelling
            and data analytics to reveal what the contemporary world — and its history —
            looks like, with depth, accuracy and inspiration.
          </p>
          <p>
            All image rights belong to their respective authors and are credited inline.
            Original materials created by Méridien are distributed under the
            Creative Commons Attribution 4.0 International
            (<span class="footer__cc">CC&nbsp;BY&nbsp;4.0</span>) license — free to use,
            share and adapt with attribution.
          </p>
        </div>
      </div>

      <div class="footer__col footer__col--right">
        <button type="button" class="footer__back-to-top" @click="onBackToTop">
          Back to top ↑
        </button>
        <ul class="footer__links">
          <li v-for="link in links" :key="link.href">
            <a :href="link.href" target="_blank" rel="noopener">{{ link.label }}</a>
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
interface FooterLink { label: string; href: string }
const links: FooterLink[] = [
  { label: 'About Méridien', href: 'https://mrdn.world/about/' },
  { label: 'Privacy Policy',  href: 'https://mrdn.world/privacy_policy/' },
  { label: 'Terms of Use',    href: 'https://mrdn.world/terms_of_use/' },
];
function onBackToTop(): void {
  if (typeof window === 'undefined') return;
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
</script>

<style scoped>
.footer {
  background: #050505;
  color: #FFF;
  width: 100%;
  display: flex;
  justify-content: center;
}
.footer__inner {
  width: 100%;
  max-width: var(--max-w);
  padding: clamp(4rem, 9vh, 8rem) clamp(1.4rem, 4vw, 4rem);
  display: grid;
  grid-template-columns: 5fr 5fr 1.85fr;
  column-gap: 4rem;
  row-gap: 2rem;
}
.footer__col { display: flex; flex-direction: column; min-width: 0; gap: 1.2rem; }
.footer__col--right { align-items: flex-start; }

.footer__brand { display: inline-block; line-height: 0; }
.footer__logo  { display: block; width: 89px; height: 51px; }

.footer__copy {
  margin: 0;
  font-family: 'Struve', sans-serif;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.7);
}
.footer__copy + .footer__copy { margin-top: -0.6rem; }

.footer__about {
  margin: 0;
  font-family: 'Struve', sans-serif;
  font-weight: 400;
  font-size: 1.06rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.7);
  max-width: 36ch;
}
.footer__about strong { color: #FFF; font-weight: 700; }
.footer__about--muted {
  color: #9F9F9F;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
}
.footer__about--muted p { margin: 0; max-width: 38ch; }

.footer__updated {
  margin: 0 0 1.4rem;
  font-family: 'Struve', sans-serif;
  font-weight: 400;
  font-size: 1rem;
  letter-spacing: 0.02em;
  color: #9F9F9F;
}

.footer__cc {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 0.5rem;
  margin: 0 0.18em;
  border: 1px solid rgba(159, 159, 159, 0.7);
  border-radius: 12px;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  line-height: 1;
  vertical-align: baseline;
}

.footer__back-to-top {
  align-self: flex-start;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: 'Struve', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  color: #9F9F9F;
  transition: color 0.15s ease;
}
.footer__back-to-top:hover { color: #FFF; }
.footer__back-to-top:focus-visible { outline: 1px solid #61E26B; outline-offset: 4px; }

.footer__links {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}
.footer__links a {
  font-family: 'Struve', sans-serif;
  font-weight: 400;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.15s ease;
}
.footer__links a:hover { color: #61E26B; }
.footer__links a:focus-visible { outline: 1px solid #61E26B; outline-offset: 2px; }

@media (max-width: 900px) {
  .footer__inner {
    grid-template-columns: 1fr;
    row-gap: 3rem;
  }
}
</style>
